import { useState, useEffect, useCallback } from 'react';
import { attemptsService } from '../services/attempts.service';
import { AttemptFiltersState, AttemptsListResponse, AttemptSummary, AttemptStats } from '../types';

export function useAttempts(initialFilters?: Partial<AttemptFiltersState>) {
  const [attempts, setAttempts] = useState<AttemptSummary[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [stats, setStats] = useState<AttemptStats>({
    total: 0,
    inProgress: 0,
    completed: 0,
    expired: 0,
    abandoned: 0,
  });
  const [filters, setFilters] = useState<AttemptFiltersState>({
    status: initialFilters?.status || '',
    search: initialFilters?.search || '',
    page: initialFilters?.page || 1,
    limit: initialFilters?.limit || 20,
    tenantId: initialFilters?.tenantId || 'all',
    userId: initialFilters?.userId || 'all',
    scope: initialFilters?.scope || 'all',
  });
  const [availableTenants, setAvailableTenants] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [availableLearners, setAvailableLearners] = useState<Array<{ id: string; name: string; email: string; role?: string; tenantId?: string }>>([]);
  const [isPrivileged, setIsPrivileged] = useState<boolean>(false);
  const [isSuper, setIsSuper] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAttempts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response: AttemptsListResponse = await attemptsService.listAttempts(filters);
      const list = response.attempts || (response as any).data?.attempts || [];
      setAttempts(list);
      setTotal(response.total ?? (response as any).data?.total ?? list.length);
      setTotalPages(response.totalPages ?? (response as any).data?.totalPages ?? 1);
      const statsObj = response.stats || (response as any).data?.stats;
      if (statsObj) {
        setStats(statsObj);
      }
      const rawData = (response as any).data || response;
      if (Array.isArray(rawData?.availableTenants)) {
        setAvailableTenants(rawData.availableTenants);
      }
      if (Array.isArray(rawData?.availableLearners)) {
        setAvailableLearners(rawData.availableLearners);
      }
      if (typeof rawData?.isPrivileged === 'boolean') {
        setIsPrivileged(rawData.isPrivileged);
      }
      if (typeof rawData?.isSuper === 'boolean') {
        setIsSuper(rawData.isSuper);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load attempts');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAttempts();
  }, [fetchAttempts]);

  const updateFilters = (newFilters: Partial<AttemptFiltersState>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      // reset to page 1 if changing status, search, tenantId, or userId
      page: newFilters.page !== undefined ? newFilters.page : (
        newFilters.status !== undefined ||
        newFilters.search !== undefined ||
        newFilters.tenantId !== undefined ||
        newFilters.userId !== undefined ||
        newFilters.scope !== undefined
          ? 1
          : prev.page
      ),
    }));
  };

  return {
    attempts,
    total,
    totalPages,
    stats,
    filters,
    availableTenants,
    availableLearners,
    isPrivileged,
    isSuper,
    loading,
    error,
    updateFilters,
    refresh: fetchAttempts,
  };
}
